import { getOperationName } from '@apollo/client/utilities'
import { useToggle } from 'ahooks'
import { Form, Input, Modal, Typography } from 'antd'
import { FormInstance } from 'antd/lib/form'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'

import {
  GetKnowledgeGroupsDocument,
  RequestedKnowledgeGroupFragment,
  useKnowledgeGroupCreateMutation,
  useKnowledgeGroupUpdateMutation,
} from '@/generated/graphql'
import { routes } from '@/routes'

interface KnowledgeGroupCommonProps {
  parentKnowledgeGroups?: { knowledgeGroupId: number; name: string }[] | null
}

const KnowledgeGroupCreateUpdateForm: React.FC<
  KnowledgeGroupCommonProps & {
    form: FormInstance<any>
    knowledgeGroup?: RequestedKnowledgeGroupFragment
  }
> = ({ form, knowledgeGroup, parentKnowledgeGroups }) => {
  const { t } = useTranslation()

  React.useEffect(() => {
    form.resetFields()
  }, [])

  const initialValues = knowledgeGroup
    ? {
        name: knowledgeGroup.name,
        description: knowledgeGroup.description,
      }
    : {
        name: '',
        description: '',
      }

  return (
    <Form
      form={form}
      labelCol={{ span: 6 }}
      wrapperCol={{ offset: 1 }}
      initialValues={initialValues}
      validateMessages={{
        required: t('group.createValidation'),
      }}
      requiredMark={!knowledgeGroup}
    >
      <Form.Item label={t('knowledge.knowledgeName')} name='name' rules={[{ required: true }]}>
        <Input />
      </Form.Item>
      <Form.Item label={t('knowledge.knowledgeParentGroup')}>
        <Typography.Text>
          {parentKnowledgeGroups
            ? parentKnowledgeGroups.map((knowledgeGroup) => knowledgeGroup.name).join(' - ')
            : t('knowledge.none')}
        </Typography.Text>
      </Form.Item>
      <Form.Item label={t('knowledge.knowledgeInfo')} name='description'>
        <Input.TextArea showCount maxLength={160} />
      </Form.Item>
    </Form>
  )
}

export const KnowledgeGroupCreateUpdateModal: React.FC<
  KnowledgeGroupCommonProps & {
    knowledgeGroup?: RequestedKnowledgeGroupFragment
  }
> = ({ knowledgeGroup, parentKnowledgeGroups, children }) => {
  const { t } = useTranslation()
  const history = useHistory()
  const [form] = Form.useForm()
  const [visible, { toggle }] = useToggle()

  const refetchQueries = {
    refetchQueries: [getOperationName(GetKnowledgeGroupsDocument)!],
  }
  const [knowledgeGroupCreate] = useKnowledgeGroupCreateMutation({
    ...refetchQueries,
  })

  const [knowledgeGroupUpdate] = useKnowledgeGroupUpdateMutation({
    ...refetchQueries,
  })

  const onOK = async () => {
    const values = await form.validateFields()

    const params = {
      name: values.name,
      description: values.description,
    }

    let group

    if (knowledgeGroup) {
      group = await knowledgeGroupUpdate({
        variables: {
          knowledgeGroupId: knowledgeGroup.knowledgeGroupId,
          knowledgeGroupInput: {
            ...params,
          },
        },
      })
    } else {
      group = await knowledgeGroupCreate({
        variables: {
          knowledgeGroupInput: {
            ...params,
            ...(!!parentKnowledgeGroups && {
              parentIds: parentKnowledgeGroups.map((knowledgeGroup) => knowledgeGroup.knowledgeGroupId),
            }),
          },
        },
      })
    }

    if (!group.data) {
      return
    }

    if ('knowledgeGroupCreate' in group.data) {
      history.push(routes.knowledgebase.category(group.data.knowledgeGroupCreate.knowledgeGroupId))
    }

    toggle()
  }

  children = React.cloneElement(children as any, {
    onClick: () => toggle(),
  })

  return (
    <div>
      {children}
      <Modal
        title={knowledgeGroup ? t('group.edit') : t('knowledge.group.create')}
        visible={visible}
        closable={false}
        maskClosable={false}
        destroyOnClose
        onCancel={() => toggle()}
        onOk={() => onOK()}
      >
        <KnowledgeGroupCreateUpdateForm
          form={form}
          knowledgeGroup={knowledgeGroup}
          parentKnowledgeGroups={parentKnowledgeGroups}
        />
      </Modal>
    </div>
  )
}
